/**
 * Mobile hamburger menu: PromoTix Official links, marketplace links, and static menu.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { DISCOVERY_PATH_PREFIX } from '../../../util/discoveryPaths';
import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { ensureCurrentUser } from '../../../util/data';
import {
  officialCreateEventUrl,
  officialLoggedInCreateEventUrl,
  officialManageEventsUrl,
  officialMyTicketsUrl,
  officialPromotixAppBaseUrl,
  resellerMarketplaceHomeUrl,
} from '../../../util/promotixNavUrls';
import { persistCurrentLocationBeforeSellerAppNavigation } from '../../../util/postSellerLoginReturnPath';

import {
  AvatarLarge,
  InlineTextButton,
  Logo,
  NamedLink,
  NotificationBadge,
} from '../../../components';

import { TopbarMobileBrowseCategoriesSection } from '../../TopbarBrowseCategoriesMegaMenu/TopbarBrowseCategoriesMegaMenu';

import css from './TopbarMobileMenu.module.css';

const DRAFT_LISTING_PARAMS = {
  slug: 'draft',
  id: '00000000-0000-0000-0000-000000000000',
  type: 'new',
  tab: 'details',
};

const MenuSectionLinks = () => (
  <ul className={css.dropdownContent}>
    <NamedLink className={css.editLinkDesktop} name="AboutPage">
      <li>
        <span>
          <FormattedMessage id="TopbarDesktopMenu.about" />
        </span>
      </li>
    </NamedLink>
    <a className={css.editLinkDesktop} href={officialCreateEventUrl()}>
      <li>
        <span>
          <FormattedMessage id="TopbarDesktopMenu.howitworkeventcreators" />
        </span>
      </li>
    </a>
    <NamedLink className={css.editLinkDesktop} name="HowItWorkPage">
      <li>
        <span>
          <FormattedMessage id="TopbarDesktopMenu.howitwork" />
        </span>
      </li>
    </NamedLink>
    <NamedLink className={css.editLinkDesktop} name="HowItWorkPageBuyers">
      <li>
        <span>
          <FormattedMessage id="TopbarDesktopMenu.howitworkbuyers" />
        </span>
      </li>
    </NamedLink>
    <a
      className={css.editLinkDesktop}
      href="https://www.promotix.com/contact"
      target="_blank"
      rel="noopener noreferrer"
    >
      <li>
        <span>
          <FormattedMessage id="TopbarDesktopMenu.contactsupport" />
        </span>
      </li>
    </a>
  </ul>
);

const BrowseEventsFooter = () => (
  <a className={css.createNewListingLink} href={resellerMarketplaceHomeUrl()}>
    <FormattedMessage id="TopbarMobileMenu.browseEventsCta" />
  </a>
);

const TopbarMobileMenu = props => {
  const {
    appConfig,
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const currentPageClass = page => {
    if (currentPage === page) {
      return css.currentPage;
    }
    if (page === 'StripePayoutPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage)) {
      return css.currentPage;
    }
    return null;
  };

  if (!isAuthenticated) {
    const sellerSignupUrl = appConfig?.sellerSignupUrl || `${DISCOVERY_PATH_PREFIX}/signup`;
    const sellerLoginUrl = appConfig?.sellerLoginUrl || `${DISCOVERY_PATH_PREFIX}/login`;

    const signup = (
      <a
        href={sellerSignupUrl}
        className={css.signupLink}
        onClick={() => persistCurrentLocationBeforeSellerAppNavigation(sellerSignupUrl)}
      >
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </a>
    );

    const login = (
      <a
        href={sellerLoginUrl}
        className={css.loginLink}
        onClick={() => persistCurrentLocationBeforeSellerAppNavigation(sellerLoginUrl)}
      >
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </a>
    );

    return (
      <div className={classNames(css.root, css.rootGuest)}>
        <div className={css.guestMenuLogoAnchor}>
          <NamedLink name="SearchPage" className={css.mobileMenuLogoLink}>
            <Logo format="mobile" className={css.mobileMenuPromoLogo} />
          </NamedLink>
        </div>
        <div className={css.content}>
          <div className={classNames(css.authenticationGreeting, css.authenticationGreetingGuest)}>
            <div className={css.unauthorizedWelcomeBrand}>
              <div className={css.unauthorizedWelcomeTitle}>
                <FormattedMessage id="TopbarMobileMenu.unauthorizedWelcomeTitle" />
              </div>
            </div>
            <br />
            <br />
            <span className={css.authenticationLinks}>
              <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
              ?
            </span>
          </div>
          <TopbarMobileBrowseCategoriesSection />
          <h2 className={classNames(css.sectionTitle, css.guestSellTicketsTitle)}>
            <FormattedMessage id="TopbarMobileMenu.sellTicketsSection" />
          </h2>
          <a
            className={classNames(css.navigationLink, css.guestPrimaryActionFirst)}
            href={officialCreateEventUrl()}
          >
            <FormattedMessage id="TopbarMobileMenu.createEventLink" />
          </a>
          <NamedLink className={css.navigationLink} name="EditListingPage" params={DRAFT_LISTING_PARAMS}>
            <FormattedMessage id="TopbarMobileMenu.sellTicketResaleLink" />
          </NamedLink>
          <div className={css.sectionDivider}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="TopbarMobileMenu.menuSectionTitle" />
            </h2>
            <MenuSectionLinks />
          </div>
        </div>
        <div className={css.footer}>
          <BrowseEventsFooter />
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <TopbarMobileBrowseCategoriesSection />

        <h2 className={css.sectionTitle}>
          <FormattedMessage id="TopbarMobileMenu.myTicketsSection" />
        </h2>
        <a className={css.navigationLink} href={officialMyTicketsUrl()}>
          <FormattedMessage id="TopbarMobileMenu.viewMyTickets" />
        </a>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.promotixEventTicketsDescription" />
        </div>
        <NamedLink className={css.navigationLink} name="InboxPage" params={{ tab: 'orders' }}>
          <FormattedMessage id="TopbarMobileMenu.marketplaceOrdersLink" />
        </NamedLink>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.marketplaceTicketsDescription" />
        </div>

        <h2 className={css.sectionTitle}>
          <FormattedMessage id="TopbarMobileMenu.marketplaceTradingSection" />
        </h2>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.marketplaceTradingSubtitle" />
        </div>
        <NamedLink className={css.navigationLink} name="EditListingPage" params={DRAFT_LISTING_PARAMS}>
          <FormattedMessage id="TopbarMobileMenu.sellTicketResaleLink" />
        </NamedLink>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.sellTicketResaleDescription" />
        </div>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
          name="ManageListingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.marketplaceListingsLink" />
        </NamedLink>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.marketplaceListingsDescription" />
        </div>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('RewardsPage'))}
          name="RewardsPage"
        >
          <FormattedMessage id="Topbar.avatarMenuCashBackRewards" />
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, css.navigationLinkWithBadge)}
          name="InboxPage"
          params={{ tab: 'sales' }}
        >
          <FormattedMessage id="TopbarMobileMenu.inboxSalesLink" />
          {notificationCountBadge}
        </NamedLink>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.inboxSalesDescription" />
        </div>

        <h2 className={css.sectionTitle}>
          <FormattedMessage id="TopbarMobileMenu.promotixCreatorsSection" />
        </h2>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.promotixCreatorsSubtitle" />
        </div>
        <a className={css.navigationLink} href={officialManageEventsUrl()}>
          <FormattedMessage id="TopbarMobileMenu.managePromotixEventsLink" />
        </a>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.managePromotixEventsDescription" />
        </div>
        <a className={css.navigationLink} href={officialLoggedInCreateEventUrl()}>
          <FormattedMessage id="TopbarMobileMenu.createEventLink" />
        </a>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.createEventDescription" />
        </div>
        <a className={css.navigationLink} href="https://www.promotix.com/ambassadors">
          <FormattedMessage id="TopbarMobileMenu.becomeAmbassador" />
        </a>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.becomeAmbassadorDescription" />
        </div>

        <h2 className={css.sectionTitle}>
          <FormattedMessage id="TopbarMobileMenu.accountSection" />
        </h2>
        <a
          className={css.navigationLink}
          href={`${officialPromotixAppBaseUrl()}/user/account-setting`}
        >
          <FormattedMessage id="Topbar.avatarMenuPromotixAccount" />
        </a>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.marketplaceAccountSettingsDescription" />
        </div>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
          name="ProfileSettingsPage"
        >
          <FormattedMessage id="Topbar.avatarMenuMarketplaceProfile" />
        </NamedLink>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.profileSettingsDescription" />
        </div>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('StripePayoutPage'))}
          name="StripePayoutPage"
        >
          <FormattedMessage id="Topbar.avatarMenuMarketplacePayout" />
        </NamedLink>
        <div className={css.sectionDescription}>
          <FormattedMessage id="TopbarMobileMenu.marketplacePaymentsPayoutsDescription" />
        </div>
        <div className={css.sectionDivider}>
          <h2 className={css.sectionTitle}>
            <FormattedMessage id="TopbarMobileMenu.menuSectionTitle" />
          </h2>
          <MenuSectionLinks />
        </div>
      </div>
      <div className={css.footer}>
        <BrowseEventsFooter />
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = {
  currentUser: null,
  notificationCount: 0,
  currentPage: null,
  appConfig: null,
};

const { bool, func, number, object, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  appConfig: object,
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
